import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  LuArrowLeft,
  LuCheck,
  LuPrinter,
  LuChevronRight,
  LuUser,
  LuCopy,
} from "react-icons/lu";
import { translateMessage } from "@delibrandev/utils";
import {
  createInvoice,
  getOrderDetails,
  printInvoice,
  printTicket,
  releaseReturnedOrder,
} from "../../api/orders";
import { setAlert } from "../../reducers/userConfigSlice";
import { formatMoney } from "../../utils/pricing";
import { TpvDialog } from "../../components/TpvDialog";
import {
  canEditConfirmedOrder,
  canRecoverOrder,
  isHomeDeliveryOrder,
} from "../../utils/orderState";
import { PickupHandover } from "./pickupHandover";
import "./orderDetailReference.css";

const states = {
  pending: "Pendiente",
  inPreparation: "En preparación",
  prepared: "Preparado",
  inDelivery: "En reparto",
  delivered: "Entregado",
  notDelivered: "No entregado",
  returned: "Devuelto",
  cancelled: "Cancelado",
  abandoned: "Abandonado",
};
const payments = { pending: "Pendiente", paid: "Pagado", failed: "Fallido" };
const events = {
  createdAt: "Pedido recibido",
  pending: "Productos añadidos al carrito",
  peding: "Productos añadidos al carrito",
  inPreparation: "En preparación",
  prepared: "Preparado",
  inDelivery: "En reparto",
  delivered: "Entregado",
  notDelivered: "No entregado",
  returned: "Devuelto",
  canceled: "Cancelado",
  cancelled: "Cancelado",
  abandoned: "Abandonado",
  reactivated: "Reactivado",
  paymentCompleted: "Pago completado",
  recoveryEmailSent: "Correo de recuperación enviado",
};
const date = (value) =>
  value && Number.isFinite(new Date(value).getTime())
    ? new Date(value).toLocaleString("es-ES", {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";
const Badge = ({ value, labels }) => (
  <span className={`tpvDetailBadge tpvDetailBadge-${value}`}>
    {labels[value] || value || "Sin registrar"}
  </span>
);
const Card = ({ title, children, aside }) => (
  <section className="tpvDetailCard" aria-label={title}>
    <div className="tpvDetailCardTitle">
      <h3>{title}</h3>
      {aside}
    </div>
    <div className="tpvDetailCardBody">{children}</div>
  </section>
);

export const OrderDetails = ({
  orderDetailsModal,
  setOrderDetailsModal,
  onOrderChanged = () => {},
  onRecover = null,
}) => {
  const dispatch = useDispatch();
  const channels = useSelector(
    (state) => state.aplicationConfig.corporationData.pointConfig.SalesChannels,
  );
  const paymentMethods = useSelector(
    (state) => state.aplicationConfig.corporationData.pointMethods?.payments,
  );
  const shippingMethods =
    useSelector(
      (state) => state.aplicationConfig.corporationData.pointMethods?.shipping,
    ) || [];
  const orderId = orderDetailsModal?.id ?? orderDetailsModal?.orderId;
  const [data, setData] = useState(null),
    [loading, setLoading] = useState(true),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [handingOver, setHandingOver] = useState(false),
    [redispatching, setRedispatching] = useState(false),
    [redispatchError, setRedispatchError] = useState(""),
    [confirmInvoice, setConfirmInvoice] = useState(false),
    [moreActions, setMoreActions] = useState(false),
    [clientView, setClientView] = useState(false),
    [copyMessage, setCopyMessage] = useState("");
  const generation = useRef(0),
    mounted = useRef(true),
    pending = useRef(false),
    redispatchPending = useRef(false);
  const getQuery = useCallback(async () => {
    const request = ++generation.current;
    setLoading(true);
    setError("");
    try {
      if (!orderId) throw Error("Pedido no válido.");
      const response = await getOrderDetails(orderId);
      if (!mounted.current || request !== generation.current) return;
      if (
        response?.error !== false ||
        !response.data ||
        (response.data.orderId && response.data.orderId !== orderId)
      )
        throw Error("No se pudo cargar el pedido.");
      setData(response.data);
    } catch (_error) {
      if (mounted.current && request === generation.current)
        setError("No se pudo cargar el pedido. Vuelve a intentarlo.");
    } finally {
      if (mounted.current && request === generation.current) setLoading(false);
    }
  }, [orderId]);
  useEffect(() => {
    mounted.current = true;
    setData(null);
    setConfirmInvoice(false);
    getQuery();
    return () => {
      mounted.current = false;
    };
  }, [getQuery]);
  const close = () => {
    if (!pending.current && !redispatchPending.current && !handingOver)
      setOrderDetailsModal(null);
  };
  const run = async (action, payload) => {
    if (pending.current || redispatchPending.current || handingOver) return;
    pending.current = true;
    setBusy(true);
    setError("");
    try {
      const response = await action(payload);
      if (response?.error !== false)
        throw Error(
          "No se pudo completar la acción. Revisa el pedido antes de reintentarlo.",
        );
      if (!mounted.current) return;
      setConfirmInvoice(false);
      dispatch(
        setAlert(translateMessage(response.message || "Acción completada")),
      );
      await getQuery();
    } catch (e) {
      if (mounted.current)
        setError(e.message || "No se pudo completar la acción.");
    } finally {
      pending.current = false;
      if (mounted.current) setBusy(false);
    }
  };
  const returnToDelivery = async () => {
    if (redispatchPending.current || pending.current || handingOver) return;
    redispatchPending.current = true;
    setRedispatching(true);
    setRedispatchError("");
    try {
      const response = await releaseReturnedOrder(data.orderId);
      if (
        response?.error !== false ||
        response.data?.order?.status !== "prepared"
      )
        throw Error();
      await getQuery();
      onOrderChanged();
    } catch (_error) {
      if (mounted.current)
        setRedispatchError(
          "No se pudo volver a poner el pedido disponible para reparto.",
        );
    } finally {
      redispatchPending.current = false;
      if (mounted.current) setRedispatching(false);
    }
  };
  const blocked = busy || handingOver || redispatching;
  const channel =
    (Array.isArray(channels) ? channels : []).find(
      (c) => c.salesChannelId === data?.salesChannelId,
    )?.name ||
    data?.SalesChannel?.name ||
    "—";
  const number =
    data?.displayNumber ||
    (data?.number != null
      ? `${data?.SalesChannel?.identifier || ""}${data.number}`
      : "—");
  const storedMethod = data?.paymentMethod;
  const configuredMethod = (
    Array.isArray(paymentMethods) ? paymentMethods : []
  ).find((entry) => entry.paymentMethodId === storedMethod);
  const method =
    configuredMethod?.name ||
    (typeof storedMethod === "string"
      ? /^[0-9a-f]{8}-[0-9a-f-]{27}$/i.test(storedMethod)
        ? "Nombre no disponible"
        : storedMethod || "Sin registrar"
      : storedMethod?.name || "Sin registrar");
  const fulfillment = data?.fulfillment?.type;
  const delivery =
    fulfillment === "pickup"
      ? "Recogida en tienda"
      : fulfillment === "delivery"
        ? "Domicilio"
        : data?.shippingMethod || "Sin registrar";
  const address = data?.address;
  const timeline = Object.entries(data?.OrderLog || {})
    .filter(
      ([key, value]) =>
        events[key] && value && Number.isFinite(new Date(value).getTime()),
    )
    .sort((a, b) => new Date(a[1]) - new Date(b[1]));
  const orderNotes = Array.isArray(data?.notes)
    ? data.notes
        .map((note) =>
          typeof note === "string" ? note : note?.note || note?.text || "",
        )
        .filter(Boolean)
        .join("\n")
    : data?.notes || data?.note || "";
  const recoverable = Boolean(onRecover) && canRecoverOrder(data);
  const driverCollects = isHomeDeliveryOrder(data, shippingMethods);
  const recover = (checkout = false) => {
    if (!blocked && recoverable && (!checkout || !driverCollects))
      onRecover(data, { checkout });
  };
  const copyPhone = async () => {
    try {
      await navigator.clipboard.writeText(
        data.phone || data.Customer?.phone || "",
      );
      setCopyMessage("Teléfono copiado");
    } catch {
      setCopyMessage(
        "No se pudo copiar. Selecciona el teléfono en los datos del cliente.",
      );
    }
  };
  const articleCount = (data?.products || []).reduce(
    (sum, product) => sum + (Number(product.quantity) || 0),
    0,
  );
  const amounts = data?.amounts;
  const invoice = data?.Invoice;
  return (
    <TpvDialog
      title={`PEDIDO ${number}`}
      className="tpvOrderDetail"
      onClose={close}
      busy={blocked}
      headerLeading={
        <button
          type="button"
          className="tpvDetailBack"
          disabled={blocked}
          onClick={close}
        >
          <LuArrowLeft aria-hidden="true" /> VOLVER
        </button>
      }
    >
      {data && (
        <div className="tpvDetailHeading">
          <dl className="tpvDetailMetadata">
            <div>
              <dt>Cliente</dt>
              <dd>{data.Customer?.fullname || "Cliente contado"}</dd>
            </div>
            <div>
              <dt>Teléfono</dt>
              <dd>{data.phone || data.Customer?.phone || "—"}</dd>
            </div>
            <div>
              <dt>Canal</dt>
              <dd>{channel}</dd>
            </div>
            <div>
              <dt>Tipo de entrega</dt>
              <dd>{delivery}</dd>
            </div>
            <div>
              <dt>Fecha y hora</dt>
              <dd>{date(data.createdAt)}</dd>
            </div>
          </dl>
          <div className="tpvDetailStatus">
            <div>
              <span>Estado del pedido</span>
              <Badge value={data.status} labels={states} />
            </div>
            <div>
              <span>Estado del pago</span>
              <Badge value={data.paymentStatus} labels={payments} />
            </div>
            <div>
              <span>Método de pago</span>
              <strong>{method}</strong>
            </div>
          </div>
        </div>
      )}
      <div className="tpvDetailScroll" aria-busy={loading}>
        {error && (
          <div role="alert" className="tpvDetailError">
            {error}{" "}
            {!busy && (
              <button type="button" onClick={getQuery}>
                Volver a cargar
              </button>
            )}
          </div>
        )}
        {loading && <p role="status">Cargando pedido…</p>}
        {data && (
          <div className="tpvDetailColumns">
            <div className="tpvDetailMain">
              <Card
                title="Productos"
                aside={
                  <span>
                    {articleCount}{" "}
                    {articleCount === 1 ? "artículo" : "artículos"}
                  </span>
                }
              >
                {(data.products || []).map((p, index) => (
                  <article
                    className="tpvDetailProduct"
                    key={p.orderProductId || p.id || index}
                  >
                    <div>
                      <strong>
                        {p.quantity} × {p.name || "Producto"}
                      </strong>
                      <strong>{formatMoney(p.lineTotalWithTax)}</strong>
                    </div>
                    {p.variantName && (
                      <p className="tpvDetailOptions">{p.variantName}</p>
                    )}
                    {Array.isArray(p.items) &&
                      p.items.map((item, i) => (
                        <p key={item.itemId || i} className="tpvDetailOptions">
                          + {item.name}
                        </p>
                      ))}
                    {typeof p.info === "string" && p.info.trim() && (
                      <p className="tpvDetailOptions">{p.info}</p>
                    )}
                    {p.note && <p className="tpvDetailNote">Nota: {p.note}</p>}
                  </article>
                ))}
                {orderNotes && (
                  <div className="tpvDetailOrderNote">
                    <strong>NOTA DEL PEDIDO</strong>
                    <p>{orderNotes}</p>
                  </div>
                )}
                {!data.products?.length && <p>No hay productos registrados.</p>}
              </Card>
              {recoverable && (
                <p className="tpvDetailEditHint">
                  {canEditConfirmedOrder(data)
                    ? "Puedes traer el pedido al TPV para modificarlo antes de que Cocina inicie una línea."
                    : driverCollects
                      ? "Cocina ya ha iniciado el pedido. Puedes consultarlo; el repartidor cobra al entregar."
                      : "Cocina ya ha iniciado el pedido. Puedes traerlo al TPV para consultarlo o cobrarlo; la edición está bloqueada."}
                </p>
              )}
              <Card title="Datos de entrega">
                <strong>{delivery}</strong>
                {address && (
                  <p>
                    {[
                      address.name,
                      address.street,
                      address.address,
                      address.number,
                      address.floor,
                      address.door,
                      address.zipCode || address.postalCode,
                      address.city,
                      address.province,
                      address.country,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                )}
                {data.email && <p>{data.email}</p>}
              </Card>
              {data.deliveryAttempts?.length > 0 && (
                <Card title="Intentos de entrega">
                  <ol>
                    {data.deliveryAttempts.map((attempt, index) => (
                      <li key={index}>
                        {attempt.userName || "Repartidor"} ·{" "}
                        {date(attempt.startedAt)} ·{" "}
                        {attempt.outcome === "returned"
                          ? "No entregado"
                          : attempt.outcome === "delivered"
                            ? "Entregado"
                            : "En reparto"}
                        {attempt.observation && <p>{attempt.observation}</p>}
                      </li>
                    ))}
                  </ol>
                </Card>
              )}
            </div>
            <aside className="tpvDetailSidebar">
              <Card title="Cronología del pedido">
                {timeline.length ? (
                  <ol className="tpvDetailTimeline">
                    {timeline.map(([key, value]) => (
                      <li key={key}>
                        <strong>{events[key]}</strong>
                        {key === data.status &&
                          !["delivered", "cancelled", "abandoned"].includes(
                            data.status,
                          ) && (
                            <span className="tpvDetailCurrent">
                              {" "}
                              · en curso
                            </span>
                          )}
                        <time dateTime={value}>{date(value)}</time>
                      </li>
                    ))}
                  </ol>
                ) : (
                  <p>No hay eventos registrados.</p>
                )}
              </Card>
              <Card title="Resumen económico">
                {amounts ? (
                  <dl className="tpvDetailAmounts">
                    {[
                      ["Subtotal sin IVA", amounts.subtotal],
                      ["Descuento sin IVA", amounts.discounts],
                      ["Base imponible", amounts.base],
                      ["Impuestos", amounts.tax],
                      ["Gastos de envío", amounts.shipping],
                      ["TOTAL", amounts.total],
                    ].map(([label, value]) => (
                      <div key={label}>
                        <dt>{label}</dt>
                        <dd>{formatMoney(value)}</dd>
                      </div>
                    ))}
                  </dl>
                ) : (
                  <p role="alert">
                    Los importes de este pedido necesitan revisión.
                  </p>
                )}
              </Card>
              <Card title="Pago">
                <dl className="tpvDetailAmounts">
                  <div>
                    <dt>Estado</dt>
                    <dd>
                      <Badge value={data.paymentStatus} labels={payments} />
                    </dd>
                  </div>
                  <div>
                    <dt>Método</dt>
                    <dd>{method}</dd>
                  </div>
                  <div>
                    <dt>Importe del pedido</dt>
                    <dd>{formatMoney(amounts?.total)}</dd>
                  </div>
                </dl>
                {recoverable && !driverCollects && (
                  <button
                    type="button"
                    className="tpvPrimary tpvDetailPay"
                    disabled={blocked || !Number.isFinite(amounts?.total)}
                    onClick={() => recover(true)}
                  >
                    COBRAR {formatMoney(amounts?.total)}
                  </button>
                )}
                {data.paymentStatus !== "paid" && (
                  <p>
                    {driverCollects
                      ? "Pendiente de cobro por el repartidor al entregar."
                      : "El pedido sigue pendiente de pago. Cobra antes de confirmar la entrega."}
                  </p>
                )}
                {data.paymentStatus === "paid" && (
                  <div className="tpvDetailPaid">
                    <LuCheck aria-hidden="true" /> PAGADO
                  </div>
                )}
              </Card>
              <Card title="Facturación">
                {invoice ? (
                  <>
                    <strong>
                      Factura {invoice.InvoiceNumber || "registrada"}
                    </strong>
                    <button
                      type="button"
                      disabled={blocked}
                      onClick={() =>
                        run(printInvoice, {
                          orderId: data.orderId,
                          isCorrective: false,
                          paymentMethods: data.paymentMethod,
                        })
                      }
                    >
                      <LuPrinter aria-hidden="true" /> Imprimir factura
                    </button>
                  </>
                ) : (
                  <>
                    <p>Sin factura registrada.</p>
                    {confirmInvoice ? (
                      <div
                        role="group"
                        aria-label="Confirmar creación de factura"
                      >
                        <p>¿Crear la factura de este pedido?</p>
                        <button
                          type="button"
                          disabled={blocked}
                          onClick={() =>
                            run(createInvoice, {
                              orderId: data.orderId,
                              isCorrective: false,
                              products: data.products,
                            })
                          }
                        >
                          Confirmar creación de factura
                        </button>
                        <button
                          type="button"
                          disabled={blocked}
                          onClick={() => setConfirmInvoice(false)}
                        >
                          Cancelar
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        disabled={blocked}
                        onClick={() => setConfirmInvoice(true)}
                      >
                        Crear factura
                      </button>
                    )}
                  </>
                )}
              </Card>
              <Card title="Devoluciones">
                <p>Las devoluciones todavía no están disponibles en el TPV.</p>
              </Card>
            </aside>
          </div>
        )}
        {redispatchError && (
          <p role="alert" className="tpvDetailError">
            {redispatchError}
          </p>
        )}
      </div>
      {data && (
        <footer>
          {recoverable && (
            <>
              {!driverCollects && (
                <button
                  type="button"
                  className="tpvPrimary"
                  disabled={blocked || !Number.isFinite(amounts?.total)}
                  onClick={() => recover(true)}
                >
                  COBRAR {formatMoney(amounts?.total)}
                </button>
              )}
              <button
                type="button"
                disabled={blocked}
                onClick={() => recover(false)}
              >
                TRAER AL TPV
              </button>
            </>
          )}
          <PickupHandover
            compact
            disabled={blocked}
            key={data.orderId}
            order={data}
            onBusyChange={setHandingOver}
            onError={(message) => {
              dispatch(setAlert(translateMessage(message)));
              getQuery();
            }}
            onDelivered={() => {
              getQuery();
              onOrderChanged();
            }}
          />
          <button
            type="button"
            className="tpvPrimary"
            disabled={blocked}
            onClick={() => run(printTicket, { orderId: data.orderId })}
          >
            <LuPrinter aria-hidden="true" />{" "}
            {busy ? "Procesando…" : "IMPRIMIR TICKET"}
          </button>
          {data.paymentStatus === "paid" && (
            <button
              type="button"
              disabled
              title="Las devoluciones todavía no están disponibles en el TPV"
            >
              HACER DEVOLUCIÓN
            </button>
          )}
          {["returned", "notDelivered"].includes(data.status) && (
            <button type="button" disabled={blocked} onClick={returnToDelivery}>
              {redispatching ? "Guardando…" : "Volver a reparto"}
            </button>
          )}
          <button
            type="button"
            className="tpvDetailMore"
            disabled={blocked}
            onClick={() => setMoreActions(true)}
          >
            Más acciones <LuChevronRight aria-hidden="true" />
          </button>
        </footer>
      )}
      {moreActions && (
        <TpvDialog
          title={`Más acciones · ${number}`}
          onClose={() => setMoreActions(false)}
          className="tpvDetailActionsDialog"
        >
          <div className="tpvDialogBody">
            <div className="tpvDetailActionList">
              <button
                type="button"
                disabled={blocked}
                onClick={() => {
                  setMoreActions(false);
                  run(printTicket, { orderId: data.orderId });
                }}
              >
                <LuPrinter aria-hidden="true" /> Reimprimir ticket{" "}
                <LuChevronRight aria-hidden="true" />
              </button>
              {recoverable && (
                <button
                  type="button"
                  onClick={() => {
                    setMoreActions(false);
                    recover(false);
                  }}
                >
                  Traer al TPV <LuChevronRight aria-hidden="true" />
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  setMoreActions(false);
                  setClientView(true);
                }}
              >
                <LuUser aria-hidden="true" /> Ver cliente{" "}
                <LuChevronRight aria-hidden="true" />
              </button>
              <button
                type="button"
                disabled={!(data.phone || data.Customer?.phone)}
                onClick={copyPhone}
              >
                <LuCopy aria-hidden="true" /> Copiar teléfono{" "}
                <LuChevronRight aria-hidden="true" />
              </button>
              <button type="button" disabled>
                Marcar incidencia · No disponible
              </button>
              <button type="button" disabled>
                Cancelar pedido · No disponible desde el TPV
              </button>
            </div>
            {copyMessage && <p role="status">{copyMessage}</p>}
          </div>
        </TpvDialog>
      )}
      {clientView && (
        <TpvDialog
          title="Datos del cliente"
          onClose={() => setClientView(false)}
        >
          <div className="tpvDialogBody">
            <h3>{data.Customer?.fullname || "Cliente contado"}</h3>
            <p>{data.phone || data.Customer?.phone || "Sin teléfono"}</p>
            <p>
              {data.email || data.Customer?.email || "Sin correo registrado"}
            </p>
            {address && (
              <p>
                {[
                  address.street,
                  address.zipCode,
                  address.city,
                  address.country,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
            )}
          </div>
        </TpvDialog>
      )}
    </TpvDialog>
  );
};
